// Which canvas (section) is active, and remembering it per §7B decision 4:
// each section owns its own scope; a section is remembered independently via a
// URL param + localStorage. FE2b only tracks the ACTIVE SECTION here — real
// per-section filter values plug into the same URL+storage mechanism in FE3.
//
// The parse is pure (readSectionParam) so it's unit-tested without a browser.

import { LAYOUT_SECTIONS } from "@catbento/shared";
import type { LayoutSection } from "@catbento/shared";

export const SECTION_STORAGE_KEY = "catbento.activeSection";
export const DEFAULT_SECTION: LayoutSection = "leagues";

function isSection(x: string | null | undefined): x is LayoutSection {
  return !!x && (LAYOUT_SECTIONS as readonly string[]).includes(x);
}

/**
 * Resolve the active section from (in order): the URL `?section=`, then a
 * remembered value, then the default. Pure — the caller passes the URL search
 * string and a storage getter, so no `window` here. An unknown value in either
 * source is ignored (falls through), never trusted.
 */
export function readSectionParam(
  search: string,
  getStored: (key: string) => string | null
): LayoutSection {
  const fromUrl = new URLSearchParams(search).get("section");
  if (isSection(fromUrl)) return fromUrl;
  const stored = getStored(SECTION_STORAGE_KEY);
  if (isSection(stored)) return stored;
  return DEFAULT_SECTION;
}

/** Read the initial section from the live browser (safe if storage is blocked). */
export function readInitialSection(): LayoutSection {
  const getStored = (key: string): string | null => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };
  return readSectionParam(window.location.search, getStored);
}

/** Persist the active section to the URL (no history entry) + localStorage, so a
 *  reload lands on the same canvas. Best-effort — a blocked store never throws. */
export function persistSection(section: LayoutSection): void {
  try {
    window.localStorage.setItem(SECTION_STORAGE_KEY, section);
  } catch {
    // storage blocked (private mode) — the URL param still carries it
  }
  const url = new URL(window.location.href);
  url.searchParams.set("section", section);
  window.history.replaceState(null, "", url);
}
